import { useMutation, useQueryClient } from '@tanstack/react-query';
import { customAxiosInstanceMutator } from './custom-axios-instance';
import type { Channel, Lead, LoanLead } from './schemas/loanServiceSchemas';

/**
 * Create a lead, saying which channel it came in on.
 *
 * The contract has carried a CHANNEL header on this endpoint all along, but orval does not
 * generate header parameters, so the generated `createLead` omits it and every lead captured
 * in the LMS was recorded as channel LMS regardless of where it actually came from. This
 * wrapper sends the header; it lives outside the generated file so regenerating the client
 * does not wipe it.
 */
export const createLeadWithChannel = (lead: Lead, channel: Channel, signal?: AbortSignal) =>
    customAxiosInstanceMutator<LoanLead>({
        url: `/loan/api/v1/lead/create`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json', CHANNEL: channel },
        data: lead,
        signal,
    });

/** Mutation hook mirroring the generated `useCreateLead`, with the channel carried through. */
export const useCreateLeadWithChannel = (options?: {
    onSuccess?: (lead: LoanLead) => void;
    onError?: (error: unknown) => void;
}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ data, channel }: { data: Lead; channel: Channel }) =>
            createLeadWithChannel(data, channel),
        onSuccess: (lead) => {
            queryClient.invalidateQueries({ queryKey: ['/loan/api/v1/leads'] });
            options?.onSuccess?.(lead);
        },
        onError: options?.onError,
    });
};
